import {
  FormContainer,
  FormTabs,
  Treturn,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import {
  createESignRule,
  getESignRuleById,
  updateESignRuleById,
} from "../../../../../services/esignRule.service";
import { esignRuleSchema, TESignRuleForm } from "./esignRuleSchema";
import { Grid } from "@mui/material";
import { BasicData } from "./BasicData/BasicData";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import MiscellaneousServicesRoundedIcon from "@mui/icons-material/MiscellaneousServicesRounded";
import { DocumentsPage } from "./DocumentsForm/DocumentsPage";
import { InteroperabilityPage } from "./InterroperabilityForm/InterroperabilityPage";

const translationPath = "4uESign.page.automations.automationRules.";

const AutomationRule = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { eI, id } = useParams();

  const { setMessage } = useNotificationContext();

  const { sendRequest: save, loading: sLoading } = useFetch(createESignRule);

  const { sendRequest: update, loading: uLoading } =
    useFetch(updateESignRuleById);

  const { sendRequest: get, loading: gLoading } = useFetch(getESignRuleById);

  const [esignAutomationCode, setEsignAutomationCode] = useState("");

  const saveHandler = (v: TESignRuleForm) => {
    return save({
      ...v,
      signOnlyId: v.signOnly.id,
      signOnlyPeriod: v.signOnlyPeriod ? v.signOnlyPeriod : 1,
      esignLocationSaveFileId: v.esignLocationSaveFile.id,
      establishments: v.esignEstablishments
        ? v.esignEstablishments.map((x) => ({
            externalId: x.id,
            externalName: x.name,
          }))
        : [],
      authorizationTypes: v.authorizationTypes?.map((authorizationType) => ({
        externalId: authorizationType.id,
        externalName: authorizationType.name,
      })),
      guideTypes: v.guideTypes?.map((guideType) => ({
        externalId: guideType.id,
        externalName: guideType.name,
      })),
      authorizationStages: v.authorizationStages?.map((authorizationStage) => ({
        externalId: authorizationStage.id,
        externalName: authorizationStage.name,
      })),
      insurances: v.insurances?.map((insurance) => ({
        externalId: insurance.id,
        externalName: insurance.name,
      })),
      esignRulePacientEncounterTypes: v.esignAttendantTypes
        ? v.esignAttendantTypes.map((x) => ({
            externalId: x.id,
            externalName: x.name,
          }))
        : [],
      requiredSignatureIds: v.requiredSignatures
        ? v.requiredSignatures.map((x) => x.id)
        : [],
      esignAutomationId: v.esignAutomation?.id ?? "",
      initialDate: v.initialDate?.toISOString().substring(0, 10),
      endDate: v.endDate?.toISOString().substring(0, 10),
      priority: Number(v.priority),
      physicians: v.physicians
        ? v.physicians.map((x) => {
            return {
              physicianId: Number(x.id),
              physicianName: x.name,
            };
          })
        : null,
      insuranceTypes: v.insuranceTypes
        ? v.insuranceTypes.map((x) => {
            return {
              insuranceTypeId: Number(x.id),
              insuranceTypeName: x.name,
            };
          })
        : null,
    });
  };
  const updateHandler = (v: TESignRuleForm, id: string) => {
    return update({
      item: {
        ...v,
        signOnlyId: v.signOnly.id,
        signOnlyPeriod: v.signOnlyPeriod ? v.signOnlyPeriod : 1,
        esignLocationSaveFileId: v.esignLocationSaveFile.id,
        establishments: v.esignEstablishments
          ? v.esignEstablishments.map((x) => ({
              externalId: x.id,
              externalName: x.name,
            }))
          : [],
        authorizationTypes: v.authorizationTypes?.map((authorizationType) => ({
          externalId: authorizationType.id,
          externalName: authorizationType.name,
        })),
        guideTypes: v.guideTypes?.map((guideType) => ({
          externalId: guideType.id,
          externalName: guideType.name,
        })),
        authorizationStages: v.authorizationStages?.map(
          (authorizationStage) => ({
            externalId: authorizationStage.id,
            externalName: authorizationStage.name,
          }),
        ),
        insurances: v.insurances?.map((insurance) => ({
          externalId: insurance.id,
          externalName: insurance.name,
        })),
        esignRulePacientEncounterTypes: v.esignAttendantTypes
          ? v.esignAttendantTypes.map((x) => ({
              externalId: x.id,
              externalName: x.name,
            }))
          : [],
        requiredSignatureIds: v.requiredSignatures
          ? v.requiredSignatures.map((x) => x.id)
          : [],
        esignAutomationId: v.esignAutomation?.id ?? "",
        initialDate: v.initialDate?.toISOString().substring(0, 10),
        endDate: v.endDate?.toISOString().substring(0, 10),
        priority: Number(v.priority),
        physicians: v.physicians
          ? v.physicians.map((x) => {
              return {
                physicianId: Number(x.id),
                physicianName: x.name,
              };
            })
          : null,
        insuranceTypes: v.insuranceTypes
          ? v.insuranceTypes.map((x) => {
              return {
                insuranceTypeId: Number(x.id),
                insuranceTypeName: x.name,
              };
            })
          : null,
      },
      id: id,
    });
  };

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TESignRuleForm>> => {
      const { data, success } = await get(id);

      if (data) {
        setEsignAutomationCode(data.esignAutomation.code ?? "");
        const newData: TESignRuleForm = {
          ...data,
          priority: data.priority,
          initialDate: new Date(data.initialDate),
          endDate: new Date(data.endDate),
          headerPdf: data.headerPdf ?? "",
          footerPdf: data.footerPdf ?? "",
          esignLocationSaveFile: {
            id: data.esignLocationSaveFile.id,
            name: data.esignLocationSaveFile.name,
            code: data.esignLocationSaveFile.code,
            description: data.esignLocationSaveFile.description,
          },
          esignEstablishments: data.esignRuleEstablishments
            ? data.esignRuleEstablishments.map((x: any) => ({
                id: x.externalId,
                name: x.externalName,
                code: null,
              }))
            : [],
          authorizationTypes: data.esignRuleAuthorizationTypes?.map(
            (authorizationType) => ({
              id: authorizationType.externalId,
              name: authorizationType.externalName,
            }),
          ),
          guideTypes: data.esignRuleGuideTypes?.map((guideType) => ({
            id: guideType.externalId,
            name: guideType.externalName,
          })),
          authorizationStages: data.esignRuleAuthotrizationStages?.map(
            (authorizationStage) => ({
              id: authorizationStage.externalId,
              name: authorizationStage.externalName,
            }),
          ),
          insurances: data.esignRuleInsurances?.map((insurance) => ({
            id: insurance.externalId,
            name: insurance.externalName,
          })),

          esignAttendantTypes: data.esignRulePacientEncounterTypes
            ? data.esignRulePacientEncounterTypes.map((x: any) => ({
                id: x.externalId,
                name: x.externalName,
                code: null,
              }))
            : [],

          requiredSignatures: data.esignRuleSignatureRequireds
            ? data.esignRuleSignatureRequireds.map((x: any) => ({
                id: x.id?.toString(),
                name: x.name ?? "Assinatura",
                code: x.code,
              }))
            : [],
          physicians: data.esignRulePhysicians
            ? data.esignRulePhysicians.map((x) => {
                return {
                  id: x.physicianId.toString(),
                  name: x.physicianName,
                };
              })
            : [],
          insuranceTypes: data.esignRuleInsuranceTypes
            ? data.esignRuleInsuranceTypes.map((x) => {
                return {
                  id: x.insuranceTypeId.toString(),
                  name: x.insuranceTypeName,
                };
              })
            : [],
        };

        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const redirectOnSuccess = useCallback(
    (data?: any) => {
      if (!id) {
        if (data) {
          setMessage({
            message: t(translationPath + "created_successfully"),
            type: "success",
          });
          navigate(
            `/e/${eI}/4uESign/createAutomations/automationRules/${data.id}/edit`,
          );
        }
      } else {
        navigate(`/e/${eI}/4uESign/createAutomations/automationRules/`);
      }
    },
    [navigate, setMessage, t, id, eI],
  );

  const loading = sLoading || uLoading || gLoading;

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        label: t(translationPath + "documents"),
        icon: <DescriptionRoundedIcon fontSize="small" />,
        component: <DocumentsPage esignAutomationCode={esignAutomationCode} />,
      },
      {
        label: t(translationPath + "interoperability"),
        icon: <MiscellaneousServicesRoundedIcon fontSize="small" />,
        component: <InteroperabilityPage />,
      },
    ];

    return baseTabs;
  }, [t, esignAutomationCode]);

  return (
    <>
      <FormContainer<TESignRuleForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        defaultValues={{
          saveQrCode: true,
        }}
        loading={loading}
        title={t(translationPath + "automation_rule")}
        schema={esignRuleSchema}
        subtitle={t(translationPath + "new_automation_rule")}
        subtitleWatchField={"name"}
        backUrl={`/e/${eI}/4uESign/createAutomations/automationRules/`}
        redirectOnSave={false}
        onSuccess={redirectOnSuccess}
        formActionsOnBottom={false}
        marginBottom={5}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <BasicData />
            </Grid>
          </Grid>
        )}
      />
      {id && (
        <Grid
          container
          spacing={2}
          sx={{ width: "80%", margin: "0 auto", zIndex: 999 }}
        >
          <Grid item sm={12} mb={5}>
            <FormTabs tabs={tabs} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default AutomationRule;
